/**
 * i18n Configuration for Visitor PWA
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NAMES,
  LANGUAGE_CODES,
  LOCALE_MAP,
  applyDocumentDirection,
  isRTL,
  type SupportedLanguage,
} from '@tendedloop/shared/i18n';

// Re-export for convenience, other visitor-pwa files import from '@/i18n'
export { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, LANGUAGE_CODES, LOCALE_MAP, isRTL, type SupportedLanguage };

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: 'common',
    ns: ['common', 'visitor', 'errors'],
    detection: {
      order: ['querystring', 'localStorage', 'htmlTag'],
      caches: ['localStorage'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json?v=20261010c',
    },
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: import.meta.env.DEV
      ? (_lngs: readonly string[], ns: string, key: string) => {
          console.warn(`[i18n] Missing translation: ${ns}:${key}`);
        }
      : undefined,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
    debug: import.meta.env.DEV,
  });

function loadEthiopicFont() {
  if (!document.getElementById('noto-sans-ethiopic')) {
    const link = document.createElement('link');
    link.id = 'noto-sans-ethiopic';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Ethiopic:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }
}

applyDocumentDirection(i18n.language);
if (i18n.language === 'am') loadEthiopicFont();

i18n.on('languageChanged', (lng) => {
  applyDocumentDirection(lng);
  if (lng === 'am') loadEthiopicFont();
});

// The visitor's own language choice (switcher or ?lang=) beats the tenant default.
// i18next caches whatever it resolved in localStorage, so it can't tell a choice
// from a fallback; this flag can.
const EXPLICIT_KEY = 'tl_lang_explicit';

export function markLanguageExplicit(): void {
  try {
    localStorage.setItem(EXPLICIT_KEY, 'true');
  } catch {
    // Private mode: the choice simply isn't remembered.
  }
}

function hasExplicitLanguage(): boolean {
  try {
    return localStorage.getItem(EXPLICIT_KEY) === 'true';
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('lang')) {
  markLanguageExplicit();
}

// One-time carry-over for visitors from before this flag existed: a stored
// non-English language could only have come from their own choice (the
// fallback is always English). Runs once, so a language later set from a
// tenant default is never mistaken for a choice.
try {
  if (localStorage.getItem('tl_lang_migrated') !== '1') {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && stored.split('-')[0] !== 'en') markLanguageExplicit();
    localStorage.setItem('tl_lang_migrated', '1');
  }
} catch {
  // Storage unavailable: nothing to carry over.
}

/** Open in the operator's chosen language (e.g. ja) unless the visitor picked one. */
export function applyTenantDefaultLanguage(code: string | undefined): void {
  if (!code || hasExplicitLanguage()) return;
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(code)) return;
  if (i18n.language?.split('-')[0] !== code) void i18n.changeLanguage(code);
}

export default i18n;
